import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { User, Briefcase, Calendar, Clock, Loader2 } from "lucide-react";
import type { UseMutationResult } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  TextInput,
  SelectDropdown,
  type OptionItem,
} from "@/components/formfields";
import { useInfiniteTeamList } from "@/pages/master-management/hooks/useTeam";
import { useInfiniteDepartmentList } from "@/pages/master-management/hooks/useDepartment";
import type { TeamDetail, DepartmentDetail } from "@/interface/masterManagement";
import type { ProfileDetail, ProfilePayload } from "@/interface/profile";
import type { ApiResponse } from "@/interface/common";

const professionalInfoSchema = z.object({
  team: z.string().min(1, "Team is required"),
  department: z.string().min(1, "Department is required"),
  job_role: z.string().trim().min(1, "Job Role is required"),
  experience_years: z.coerce
    .number()
    .min(0, "Experience years cannot be negative")
    .optional()
    .nullable(),
  experience_months: z.coerce
    .number()
    .min(0, "Months must be at least 0")
    .max(11, "Months must be at most 11")
    .optional()
    .nullable(),
  joining_date: z.string().trim().optional().nullable(),
  reporting_manager: z.string().trim().optional().nullable(),
});

export type ProfessionalInfoFormValues = z.infer<typeof professionalInfoSchema>;

interface ProfessionalInfoSectionProps {
  profile?: ProfileDetail;
  patchProfile?: UseMutationResult<
    ApiResponse<ProfileDetail>,
    Error,
    { id: number | string; payload: Partial<ProfilePayload> }
  >;
}

export function ProfessionalInfoSection({
  profile,
  patchProfile,
}: ProfessionalInfoSectionProps) {
  const [teamSearch, setTeamSearch] = useState<string>("");
  const [debouncedTeamSearch, setDebouncedTeamSearch] = useState<string>("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedTeamSearch(teamSearch);
    }, 350);
    return () => clearTimeout(timer);
  }, [teamSearch]);

  const {
    data: teamData,
    isLoading: isTeamsLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteTeamList({
    page: 1,
    page_size: 10,
    search: debouncedTeamSearch || undefined,
  });

  const teamOptions: OptionItem[] = useMemo(() => {
    const list = teamData?.pages
      ? teamData.pages.flatMap((page) => {
          const items = Array.isArray(page?.data)
            ? page.data
            : page?.data?.results || [];
          return items
            .map((t: TeamDetail) => ({
              label: t.team_name || "",
              value: String(t.id),
            }))
            .filter((opt: OptionItem) => Boolean(opt.label));
        })
      : [];

    if (
      profile?.team_detail &&
      profile.team_detail.id &&
      !list.some((opt) => opt.value === String(profile.team_detail!.id))
    ) {
      list.unshift({
        label: profile.team_detail.team_name || String(profile.team_detail.id),
        value: String(profile.team_detail.id),
      });
    }

    return list;
  }, [teamData, profile?.team_detail]);

  const [deptSearch, setDeptSearch] = useState<string>("");
  const [debouncedDeptSearch, setDebouncedDeptSearch] = useState<string>("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedDeptSearch(deptSearch);
    }, 350);
    return () => clearTimeout(timer);
  }, [deptSearch]);

  const {
    data: departmentData,
    isLoading: isDepartmentsLoading,
    fetchNextPage: fetchNextDeptPage,
    hasNextPage: hasDeptNextPage,
    isFetchingNextPage: isFetchingDeptNextPage,
  } = useInfiniteDepartmentList({
    page: 1,
    page_size: 10,
    search: debouncedDeptSearch || undefined,
  });

  const departmentOptions: OptionItem[] = useMemo(() => {
    const list = departmentData?.pages
      ? departmentData.pages.flatMap((page) => {
          const items = Array.isArray(page?.data)
            ? page.data
            : page?.data?.results || [];
          return items
            .map((d: DepartmentDetail) => ({
              label: d.department_name || "",
              value: String(d.id),
            }))
            .filter((opt: OptionItem) => Boolean(opt.label));
        })
      : [];

    if (
      profile?.department_detail &&
      profile.department_detail.id &&
      !list.some((opt) => opt.value === String(profile.department_detail!.id))
    ) {
      list.unshift({
        label:
          profile.department_detail.department_name ||
          String(profile.department_detail.id),
        value: String(profile.department_detail.id),
      });
    }

    return list;
  }, [departmentData, profile?.department_detail]);

  const { control, handleSubmit, reset, setValue } =
    useForm<ProfessionalInfoFormValues>({
      resolver: zodResolver(professionalInfoSchema),
      defaultValues: {
        team: "",
        department: "",
        job_role: "",
        experience_years: 0,
        experience_months: 0,
        joining_date: "",
        reporting_manager: "",
      },
    });

  useEffect(() => {
    if (profile) {
      const teamVal = profile.team_detail?.id ?? "";
      const deptVal = profile.department_detail?.id ?? "";

      reset({
        team: teamVal ? String(teamVal) : "",
        department: deptVal ? String(deptVal) : "",
        job_role: profile.job_role || "",
        experience_years: profile.experience_years ?? 0,
        experience_months: profile.experience_months ?? 0,
        joining_date: profile.joining_date || "",
        reporting_manager: profile.reporting_manager_detail?.name || "",
      });
    }
  }, [profile, reset]);

  const onSubmit = (data: ProfessionalInfoFormValues) => {
    if (!profile?.id) return;
    const payload: Partial<ProfilePayload> = {
      ...data,
      team: Number(data.team) || data.team,
      department: Number(data.department) || data.department,
    };

    if (patchProfile?.mutate) {
      patchProfile.mutate({ id: profile.id, payload });
    }
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6 shadow-sm space-y-8"
    >
      <div className="flex justify-between items-center border-b border-slate-100 dark:border-slate-800 pb-4">
        <div>
          <h2 className="text-lg font-bold text-primary">
            Professional Information
          </h2>
          <p className="text-xs text-slate-500">
            Edit candidate professional details
          </p>
        </div>
        <Button
          type="submit"
          disabled={patchProfile?.isPending}
          className="bg-amber-500 hover:bg-amber-600 text-black font-bold border-0 text-xs h-9 px-4"
        >
          {patchProfile?.isPending && (
            <Loader2 className="w-4 h-4 mr-2 animate-spin text-black" />
          )}
          Save Changes
        </Button>
      </div>

      <section className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Team Dropdown */}
          <div>
            <SelectDropdown
              control={control}
              name="team"
              label="Team *"
              options={teamOptions}
              placeholder={isTeamsLoading ? "Loading teams..." : "Select Team"}
              onChange={(val) => setValue("team", String(val))}
              isSearchable
              onSearch={(query) => setTeamSearch(query)}
              onMenuScrollToBottom={() => {
                if (hasNextPage && !isFetchingNextPage) {
                  fetchNextPage();
                }
              }}
              hasMore={hasNextPage}
              isLoadingMore={isFetchingNextPage}
            />
          </div>

          {/* Department Dropdown */}
          <div>
            <SelectDropdown
              control={control}
              name="department"
              label="Department *"
              options={departmentOptions}
              placeholder={
                isDepartmentsLoading
                  ? "Loading departments..."
                  : "Select Department"
              }
              onChange={(val) => setValue("department", String(val))}
              isSearchable
              onSearch={(query) => setDeptSearch(query)}
              onMenuScrollToBottom={() => {
                if (hasDeptNextPage && !isFetchingDeptNextPage) {
                  fetchNextDeptPage();
                }
              }}
              hasMore={hasDeptNextPage}
              isLoadingMore={isFetchingDeptNextPage}
            />
          </div>

          {/* Job Role */}
          <div>
            <TextInput
              control={control}
              name="job_role"
              label="Job Role"
              required
              prefix={<Briefcase className="w-4 h-4 text-slate-400" />}
              placeholder="e.g. Senior Software Engineer"
            />
          </div>

          {/* Experience */}
          <div>
            <div className="grid grid-cols-2 gap-2">
              <TextInput
                control={control}
                name="experience_years"
                label="Exp. (Years)"
                type="number"
                prefix={<Clock className="w-4 h-4 text-slate-400" />}
                placeholder="0"
              />
              <TextInput
                control={control}
                name="experience_months"
                label="Exp. (Months)"
                type="number"
                placeholder="0"
              />
            </div>
          </div>

          {/* Joining Date */}
          <div>
            <TextInput
              control={control}
              name="joining_date"
              label="Joining Date"
              type="date"
              prefix={<Calendar className="w-4 h-4 text-slate-400" />}
            />
          </div>

          {/* Reporting Manager */}
          <div>
            <TextInput
              control={control}
              name="reporting_manager"
              label="Reporting Manager"
              prefix={<User className="w-4 h-4 text-slate-400" />}
              placeholder="e.g. Jane Doe"
            />
          </div>
        </div>
      </section>
    </form>
  );
}
